import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { Button } from 'components/common'
import { booleanValueFormatter } from 'components/orderManagement'
import { ORDER_TABLE_FIELDS as OK, ORDER_STATUS_CLASSES } from 'utils/constants'
import { RMA_KEYS } from 'utils/constants/rma'
import {
  conditionalDateFormatter,
  convertTo12HourFormat,
} from 'utils/helpers/date'

const valueFormatter = (params: ValueFormatterParams) => params.value || '-'

type ResyncCellRendererProps = {
  params: ICellRendererParams
  onResync: (params: ICellRendererParams) => void
}

const ResyncCellRenderer = ({ params, onResync }: ResyncCellRendererProps) => {
  return (
    <div className="flex h-full items-center justify-center">
      <Button
        size="small"
        variant="outline"
        className="w-24"
        buttonText="Resync"
        type="button"
        onClick={() => onResync(params)}
      />
    </div>
  )
}

export const getPendingErpSyncOrderColumns = (
  onResync: (params: ICellRendererParams) => void,
  isEditor: boolean,
): ColDef[] => [
  { headerName: OK.ORDER_NO.label, field: OK.ORDER_NO.value, minWidth: 150 },
  {
    headerName: OK.STATUS.label,
    field: OK.STATUS.value,
    minWidth: 200,
    cellRenderer: ({ value }: ICellRendererParams) => {
      return (
        <span
          className={`rounded-lg px-2 py-1 font-sans text-sm font-bold  ${
            ORDER_STATUS_CLASSES[value!]
          }`}
        >
          {value}
        </span>
      )
    },
  },
  {
    headerName: OK.AGGREGATOR_CODE.label,
    field: OK.AGGREGATOR_CODE.value,
    minWidth: 130,
  },
  {
    headerName: OK.STORE_CODE.label,
    field: OK.STORE_CODE.value,
    minWidth: 130,
  },
  {
    headerName: OK.STORE_NAME.label,
    field: OK.STORE_NAME.value,
    minWidth: 150,
  },
  {
    headerName: OK.ORDER_PLACE_DATETIME.label,
    field: OK.ORDER_PLACE_DATETIME.value,
    minWidth: 170,
    valueFormatter: conditionalDateFormatter,
  },
  {
    headerName: OK.ORDER_POSTING_DATE.label,
    field: OK.ORDER_POSTING_DATE.value,
    minWidth: 170,
    valueFormatter: (params: ValueFormatterParams) =>
      convertTo12HourFormat(params.value),
  },
  {
    headerName: OK.PLANNED_DELIVERY_DATE.label,
    field: OK.PLANNED_DELIVERY_DATE.value,
    minWidth: 170,
    valueFormatter: conditionalDateFormatter,
  },
  {
    headerName: 'Order Total',
    field: OK.ORDER_VALUE_INCLV_TAX.value,
    valueFormatter,
  },
  {
    headerName: 'Action',
    field: 'save',
    minWidth: 140,
    sortable: false,
    filter: false,
    hide: !isEditor,
    cellRenderer: (params: ICellRendererParams) => (
      <ResyncCellRenderer params={params} onResync={onResync} />
    ),
  },
]

export const getPendingErpSyncRmaColumns = (
  onResync: (params: ICellRendererParams) => void,
  isEditor: boolean,
): ColDef[] => [
  {
    headerName: RMA_KEYS.ORDER_NO.label,
    field: RMA_KEYS.ORDER_NO.value,
    minWidth: 150,
  },
  {
    headerName: RMA_KEYS.RMA_NO.label,
    field: RMA_KEYS.RMA_NO.value,
    minWidth: 150,
  },
  {
    headerName: RMA_KEYS.CREATED_BY.label,
    field: RMA_KEYS.CREATED_BY.value,
    minWidth: 150,
  },
  {
    headerName: RMA_KEYS.PAYMENTS.label,
    field: RMA_KEYS.PAYMENTS.value,
    minWidth: 220,
    autoHeight: true,
    cellRenderer: ({ value = [] }) => {
      if (!value.length) {
        return <span className="text-xs text-gray-400">--</span>
      }

      return (
        <div className="flex flex-col gap-1 py-1">
          {value.map(
            (
              payment: {
                paymentType?: string
                amount?: number
              },
              index: number,
            ) => (
              <div
                key={`${payment.paymentType}-${index}`}
                className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
              >
                <span className="truncate font-medium text-gray-700">
                  {payment.paymentType || '--'}
                </span>

                <span className="font-semibold text-gray-900">
                  {payment.amount?.toFixed(2) || '0.00'}
                </span>
              </div>
            ),
          )}
        </div>
      )
    },
  },
  {
    headerName: RMA_KEYS.CREATED_AT.label,
    field: RMA_KEYS.CREATED_AT.value,
    valueFormatter: conditionalDateFormatter,
  },
  {
    headerName: RMA_KEYS.IS_FULL_RMA.label,
    field: RMA_KEYS.IS_FULL_RMA.value,
    minWidth: 150,
    cellRenderer: booleanValueFormatter,
  },
  {
    headerName: 'Action',
    field: 'save',
    minWidth: 140,
    sortable: false,
    filter: false,
    hide: !isEditor,
    cellRenderer: (params: ICellRendererParams) => (
      <ResyncCellRenderer params={params} onResync={onResync} />
    ),
  },
]

export const getPendingErpSyncReconColumns = (
  onResync: (params: ICellRendererParams) => void,
  isEditor: boolean,
): ColDef[] => [
  { headerName: OK.ORDER_NO.label, field: 'orderNo', minWidth: 150 },
  {
    headerName: 'Aggregator',
    field: 'reconciliations.0.aggregator',
    minWidth: 130,
  },
  {
    headerName: 'Store',
    field: 'reconciliations.0.store',
    minWidth: 130,
  },
  {
    headerName: 'Type',
    field: 'reconciliations.0.reconciliationType',
    minWidth: 130,
  },
  {
    headerName: 'Created At',
    field: 'reconciliations.0.createdAt',
    minWidth: 170,
    valueFormatter: conditionalDateFormatter,
  },
  {
    headerName: 'Payment',
    field: 'reconciliations',
    minWidth: 220,
    autoHeight: true,
    cellRenderer: ({ value = [] }) => {
      if (!value.length) {
        return <span>-</span>
      }

      return (
        <div className="flex flex-col gap-1 py-1">
          {value.map(
            (
              payment: {
                originalPaymentType?: string
                amount?: number
              },
              index: number,
            ) => (
              <div
                key={`${payment.originalPaymentType}-${index}`}
                className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
              >
                <span className="truncate font-medium text-gray-700">
                  {payment.originalPaymentType || '--'}
                </span>

                <span className="font-semibold text-gray-900">
                  {payment.amount?.toFixed(2) || '0.00'}
                </span>
              </div>
            ),
          )}
        </div>
      )
    },
  },
  {
    headerName: 'Action',
    field: 'save',
    minWidth: 140,
    sortable: false,
    filter: false,
    hide: !isEditor,
    cellRenderer: (params: ICellRendererParams) => (
      <ResyncCellRenderer params={params} onResync={onResync} />
    ),
  },
]
